import { camelCase, snakeCase, capitalize, lowerCase } from "lodash/fp";
import { DateTime } from "luxon";

import { RedirectTo } from "@app/common/types";
import Config from "@app/config";

const BOT_PATH_REGEX = /^\/bot[?/]/g;

// Giving an ISO formatted date string, parses that string into a
// local Date object. Interprets the dateString as a UTC string
export const parseUTCDateFromString = (dateString: string): Date => {
  let utcDateString = dateString;
  // Matches timestamps without tz information, the regex looks for an
  // offset like +01:00 or -07:00
  if (!dateString.endsWith("Z") && !dateString.match(".*:..*[+-]")) {
    utcDateString = `${utcDateString}Z`;
  }
  utcDateString = utcDateString.replace(" ", "T");

  return new Date(utcDateString);
};

export const parseUTCDateTime = (dateToParse: string | number): DateTime => {
  if (typeof dateToParse === "number") {
    return DateTime.fromMillis(dateToParse);
  }
  const jsDate = parseUTCDateFromString(dateToParse);
  return DateTime.fromJSDate(jsDate);
};

export const parseUTCDateTimeFromString = (dateString: string): DateTime => {
  const date = parseUTCDateFromString(dateString);
  return DateTime.fromJSDate(date);
};

export const optionalParseUTCDateTimeFromString = (
  dateString?: string | null
): DateTime | undefined => {
  return dateString ? parseUTCDateTimeFromString(dateString) : undefined;
};

export const parseLocalDateTimeFromString = (dateString: string): DateTime => {
  return DateTime.fromISO(dateString);
};

export const parseUTCDateTimeToTimezoneFromString = (
  dateString: string,
  zone?: string
): DateTime => {
  if (!zone) return parseUTCDateTimeFromString(dateString);

  const date = parseUTCDateFromString(dateString);
  return DateTime.fromJSDate(date, { zone });
};

export const parseIntOrDefault = (value: any, defaultValue = -1): number => {
  const parsedValue = parseInt(value);
  return isFinite(parsedValue) ? parsedValue : defaultValue;
};

// skip whitespace will skip keys that have whitespace
export const transformKeys =
  (keyFunc: (key: string) => string, skipWhitespace = false) =>
  (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(transformKeys(keyFunc, skipWhitespace));
    } else if (obj != null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [skipWhitespace && key.includes(" ") ? key : keyFunc(key)]:
            transformKeys(keyFunc, skipWhitespace)(obj[key]),
        }),
        {}
      );
    }
    return obj;
  };

export const camelizeKeys = transformKeys(camelCase, true);
export const snakeKeys = transformKeys(snakeCase, true);

interface Titleize {
  (item: string): string;
  (item: object): object;
}

export const startCase = (item: string) => item.replace(/\w+/g, capitalize);

export const titleize: Titleize = (item: any) => {
  if (typeof item === "string") {
    return startCase(lowerCase(item));
  }

  return transformKeys((key) => titleize(key))(item);
};

export const getStringType = (value: any): string | null => {
  if (typeof value === "string") return value;
  return null;
};

/**
 * Takes in a path and composes the corresponding url w/ protocol. If no app is passed,
 * will default to the current `window.origin`.
 * @example
 *  getAppUrl("signup") or getAppUrl("/signup") => "https://app.read.ai/signup"
 */
export const getAppUrl = (path: string, opts?: { app: "booking" | "main" }) => {
  if (path.includes("http")) return path;

  const urlPath = path.startsWith("/") ? path : `/${path}`;

  const rootPath = opts
    ? opts.app === "booking"
      ? Config.RSS_LINKS_URL
      : Config.FRONTEND_URL
    : window.origin;

  return `${rootPath}${urlPath}`;
};

export const getApiUrl = (path: string) => {
  if (path.includes("http")) return path;
  const urlPath = path.startsWith("/") ? path : `/${path}`;
  const rootPath = Config.api.HOST;
  return `${rootPath}${urlPath}`;
};

export const getRelativeUrl = (path: string) => {
  if (path.includes("http")) {
    return path.replace(window.origin, "");
  }
  return path;
};

/** Returns corresponding url for a path, minus the protocol. Used for text/display purposes */
export const getDisplayAppUrl = (path: string) => {
  return getAppUrl(path).replace(/(^\w+:|^)\/\//, "");
};

export type InternalUserAccessFunction = (email?: string | null) => boolean;
/**
 * Insecurely returns if the user is an internal Read user.
 * use at your own risk!
 *
 * @param email
 */
export const isReadUserEmail: InternalUserAccessFunction = (email) =>
  !!email && email.endsWith("@read.ai");

export const invariant = (cond: any, message: string): asserts cond => {
  if (!cond) throw new Error(message);
};

export const warning = (cond: any, message: string): void => {
  if (!cond) {
    // eslint-disable-next-line no-console
    if (typeof console !== "undefined") console.warn(message);

    try {
      // This error is thrown as a convenience so you can more easily
      // find the source for a warning that appears in the console by
      // enabling "pause on exceptions" in your JavaScript debugger.
      throw new Error(message);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }
};

export const isValidUrl = (link: string | null) => {
  if (!link) {
    return false;
  }

  const urlPattern = new RegExp(
    "(?:https?)://(w+:?w*)?(S+)(:d+)?(/|/([w#!:.?+=&%!-/]))?"
  );

  return !!urlPattern.test(link);
};

export const isLinkExternal = (link?: RedirectTo | null) => {
  if (!link?.pathname || !isValidUrl(link.pathname)) {
    return false;
  }

  if (
    link.search &&
    new URLSearchParams(link.search).get("externalRedirect") === "true"
  ) {
    return true;
  }

  const url = new URL(decodeURIComponent(link.pathname));
  return url.origin !== window.location.origin;
};

/** Formats number into friendly number (e.g., 1000 -> 1,000) */
export const formatNumber = (num: number) => {
  const formatter = new Intl.NumberFormat("en-US");
  return formatter.format(num);
};

interface FormatCurrencyOptions {
  digits?: number;
  /** If true, will append "USD" to currency (defaults to true) */
  withUSD?: boolean;
}

export const formatCurrency = (
  price: number,
  options: FormatCurrencyOptions = { withUSD: true }
) => {
  const shouldForceDigits = Math.round(price) != price;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: shouldForceDigits ? 2 : options.digits,
  });
  return `${formatter.format(price)}${options.withUSD ?? true ? " USD" : ""}`;
};

export const formatCurrencyFromCents = (
  priceInCents: number,
  options?: FormatCurrencyOptions
) => formatCurrency(priceInCents / 100, options);

export const isBotTraffic = (path: string): boolean => {
  return BOT_PATH_REGEX.test(path);
};

export const isPromiseFulfilled = <T>(
  response: PromiseSettledResult<T>
): response is PromiseFulfilledResult<T> => response.status === "fulfilled";

export const isPromiseRejected = <T>(
  response: PromiseSettledResult<T>
): response is PromiseRejectedResult => response.status === "rejected";

export const getValueFromPromiseResult = <T>(
  response: PromiseSettledResult<T>,
  defaultValue: T
): T => {
  return isPromiseFulfilled(response) ? response.value : defaultValue;
};

export const getListFromPromiseResult = <T>(
  response: PromiseSettledResult<T[]>
): T[] => {
  return getValueFromPromiseResult(response, []);
};
